import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import {
  AI_AGENT_MANUAL_UPLOAD_CHANNELS,
  manualUploadChannelOptions,
} from '../../../../../../../shared/constants/aiAgentOptions';
import { AI_AGENT_HISTORY_TYPES, manualHistoryTypeOptions } from '../../../../../../../shared/constants/typeOptions';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { getDateRangeValidationError } from '../../../../../../../shared/utils/time';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IGetManualHistoryParams } from '../../../../../../../shared/apis/ai-agent/manual/types';
import type { IAiAgentManualHistoryFilterForm } from '../type';

const initialFilterForm: IAiAgentManualHistoryFilterForm = {
  startDate: null,
  endDate: null,
  serviceChannels: [],
  types: [],
};

const initialSearchType = 'regId';

const parseDateParam = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  return new Date(`${value}Z`);
};

const formatDateParam = (date: Date | null, isEndOfDay = false): string => {
  if (!date) {
    return '';
  }

  return (isEndOfDay ? dayjs(date).endOf('day') : dayjs(date).startOf('day')).toISOString().substring(0, 19);
};

const isAiAgentManualUploadChannel = (value: string): value is IAiAgentManualHistoryFilterForm['serviceChannels'][number] => {
  return value !== AI_AGENT_MANUAL_UPLOAD_CHANNELS.ALL && manualUploadChannelOptions.some((option) => option.value === value);
};

const isAiAgentManualHistoryType = (value: string): value is IAiAgentManualHistoryFilterForm['types'][number] => {
  return value !== AI_AGENT_HISTORY_TYPES.ALL && manualHistoryTypeOptions.some((option) => option.value === value);
};

const parseFilterValues = <T extends string>(value: string | null, isValidValue: (item: string) => item is T): T[] => {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(isValidValue),
    ),
  );
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useAiAgentManualHistoryFilters(companyId: number, fileUploadId: number) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentManualHistoryFilterForm>(initialFilterForm);
  const [searchType, setSearchType] = useState(initialSearchType);

  const searchTypeParam = searchParams.get('searchType') || initialSearchType;
  const keywordParam = searchParams.get('keyword') || '';
  const startDateParam = searchParams.get('modStartDt') || searchParams.get('startDate') || '';
  const endDateParam = searchParams.get('modEndDt') || searchParams.get('endDate') || '';
  const serviceChannelParam = searchParams.get('serviceChannel');
  const typeParam = searchParams.get('type');
  const serviceChannels = parseFilterValues(serviceChannelParam, isAiAgentManualUploadChannel);
  const historyTypes = parseFilterValues(typeParam, isAiAgentManualHistoryType);

  const queryParams: IGetManualHistoryParams = {
    companyId,
    fileUploadId,
    startDt: startDateParam,
    endDt: endDateParam,
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), 10),
    regId: keywordParam,
    regServiceChannels: serviceChannels.join(','),
    historyTypes: historyTypes.join(','),
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentManualHistoryFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const handleSearchType = (value: string | null) => {
    setSearchType(value || initialSearchType);
  };

  const handlePageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const applyFilters = () => {
    const dateRangeValidationError = getDateRangeValidationError(filterForm.startDate, filterForm.endDate);

    if (dateRangeValidationError) {
      void Swal.fire({
        icon: 'error',
        ...dateRangeValidationError,
        confirmButtonText: '확인',
      });

      return;
    }

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        modStartDt: formatDateParam(filterForm.startDate),
        modEndDt: formatDateParam(filterForm.endDate, true),
        serviceChannel: filterForm.serviceChannels.join(','),
        type: filterForm.types.join(','),
        searchType,
        keyword: inputRef.current?.value.trim() || '',
      }),
    );
  };

  const handleSearch = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    if ('key' in event && event.key !== 'Enter') {
      return;
    }

    applyFilters();
  };

  const resetFilters = () => {
    resetSearchInput();
    setSearchType(initialSearchType);
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        modStartDt: '',
        modEndDt: '',
        serviceChannel: '',
        type: '',
        searchType: initialSearchType,
        keyword: '',
      }),
    );
  };

  useEffect(() => {
    setSearchType(searchTypeParam === initialSearchType ? searchTypeParam : initialSearchType);
    setFilterForm({
      startDate: parseDateParam(startDateParam),
      endDate: parseDateParam(endDateParam),
      serviceChannels: parseFilterValues(serviceChannelParam, isAiAgentManualUploadChannel),
      types: parseFilterValues(typeParam, isAiAgentManualHistoryType),
    });

    if (inputRef.current) {
      inputRef.current.value = keywordParam;
    }
  }, [endDateParam, keywordParam, searchTypeParam, serviceChannelParam, startDateParam, typeParam]);

  return {
    filterForm,
    queryParams,
    searchType,
    inputRef,
    handlePageChange,
    updateFilterForm,
    handleSearchType,
    handleSearch,
    resetSearchInput,
    resetFilters,
  };
}
